import React from 'react';
import {
  ChatBubbleOutline as ChatIcon,
  CampaignOutlined as AnnounceIcon,
  ArticleOutlined as NewsletterIcon,
  PeopleOutline as DirectoryIcon,
  ConfirmationNumberOutlined as TicketIcon,
  ContactlessOutlined as TapIcon,
  Diversity3Outlined as GroupsIcon,
  PaymentsOutlined as DuesIcon,
  VolunteerActivismOutlined as FundIcon,
  StorefrontOutlined as MarketIcon,
  HowToVoteOutlined as VoteIcon,
  FolderOutlined as DocsIcon,
  AdminPanelSettingsOutlined as AdminIcon,
  UploadFileOutlined as ImportIcon,
  CheckCircleOutline as CheckIcon,
} from '@mui/icons-material';
import { A, BRAND, CRIMSON, GOLD, TENANTS, ROOT_DOMAIN, Lnk, StoreBadges, usePageMeta } from './shared';
import { SOLUTIONS } from './solutionsData';

const FEATURE_COLS = [
  {
    h: 'Talk',
    d: 'Keep every member in the conversation.',
    items: [
      { Icon: ChatIcon, t: 'Group and direct chat', d: 'Chapter, committee, and one-to-one threads, with @mentions.' },
      { Icon: AnnounceIcon, t: 'Announcements', d: 'Reach exactly the chapters, regions, or groups that need to know.' },
      { Icon: NewsletterIcon, t: 'Newsletters', d: 'Write once, send to the right audience, read anywhere.' },
      { Icon: DirectoryIcon, t: 'Member directory', d: 'Find members across chapters and graduating classes.' },
    ],
  },
  {
    h: 'Gather',
    d: 'Get people in the room, and through the door.',
    items: [
      { Icon: TicketIcon, t: 'Events and ticketing', d: 'RSVPs, paid tickets, and QR check-in.' },
      { Icon: TapIcon, t: 'Tap to pay at the door', d: 'Sell tickets on a phone, no card reader needed.' },
      { Icon: GroupsIcon, t: 'Groups', d: 'Committees and interest groups with their own events and chat.' },
    ],
  },
  {
    h: 'Fund',
    d: 'Collect what keeps the lights on.',
    items: [
      { Icon: DuesIcon, t: 'Dues', d: 'Collect online, and record Zelle, Venmo, or cash payments too.' },
      { Icon: FundIcon, t: 'Fundraising', d: 'Campaigns with goals and progress members can watch.' },
      { Icon: MarketIcon, t: 'Marketplace', d: 'Members sell merch and paraphernalia to each other.' },
    ],
  },
  {
    h: 'Govern',
    d: 'Run the organization, not the spreadsheet.',
    items: [
      { Icon: VoteIcon, t: 'Voting and elections', d: 'Secret ballots and ranked-choice, scoped to who is eligible.' },
      { Icon: DocsIcon, t: 'Document library', d: 'Bylaws, minutes, and forms in one place.' },
      { Icon: AdminIcon, t: 'Admin console', d: 'Roles, approvals, and reporting for every level.' },
      { Icon: ImportIcon, t: 'Member import', d: 'Bring your roster over from a CSV in minutes.' },
    ],
  },
];

const SETUP = [
  { t: 'Name your community', d: `Pick an address like yourorg.${ROOT_DOMAIN} and tell us who you are.` },
  { t: 'Make it yours', d: 'Upload your logo and set your colors. Members see your brand, not ours.' },
  { t: 'Map your structure', d: 'Add your national, regional, and chapter levels, or keep it to one chapter.' },
  { t: 'Invite your members', d: 'Import a CSV or send invites. Members download the app and they’re in.' },
];

/** CSS-built phone showing a generic community home screen. */
const HeroPhone: React.FC = () => (
  <div className="phone" role="img" aria-label="The Konklave app showing a community home screen with an open vote, an event ticket, and paid dues">
    <div className="screen" aria-hidden="true">
      <div className="scr-head">
        <div className="org"><i>ΣΘ</i>Sigma Theta Sorority</div>
        <h5>Good evening, Danielle</h5>
      </div>
      <div className="scr-body">
        <div className="scr-card">
          <span className="ic"><VoteIcon /></span>
          <div><b>Chapter president</b><span>Ranked-choice · closes Fri</span></div>
          <em className="tag" style={{ fontStyle: 'normal' }}>Vote</em>
        </div>
        <div className="scr-card">
          <span className="ic"><TicketIcon /></span>
          <div><b>Founders’ Day Gala</b><span>Sat, Apr 12 · 2 tickets</span></div>
        </div>
        <div className="scr-card">
          <span className="ic"><DuesIcon /></span>
          <div><b>Spring dues</b><span>Pacific Region</span></div>
          <em className="tag ok" style={{ fontStyle: 'normal' }}>Paid</em>
        </div>
        <div className="scr-card">
          <span className="ic"><NewsletterIcon /></span>
          <div><b>The Spring Bulletin</b><span>From national HQ · 3 min read</span></div>
        </div>
      </div>
      <div className="scr-tabs"><span className="on" /><span /><span /><span /><span /></div>
    </div>
  </div>
);

const Home: React.FC = () => {
  usePageMeta('One app for your whole organization');
  return (
    <>
      {/* Hero */}
      <section className="hero">
        <div className="container hero-inner">
          <div className="hero-copy">
            <h1>Your whole organization, gathered in one app.</h1>
            <p className="lead">
              {BRAND} gives fraternities, sororities, civic groups, and alumni networks one place
              to talk, meet, pay dues, and vote, from the national office down to every chapter.
            </p>
            <div className="hero-cta">
              <Lnk to="/get-started" className="btn btn-primary">Create your community</Lnk>
              <Lnk to="/sign-in" className="btn btn-outline">Sign in</Lnk>
            </div>
            <div className="hero-sub">
              <p>Already a member? Get the app and find your community.</p>
              <StoreBadges />
            </div>
          </div>

          <div className="hero-visual">
            <svg className="ring" viewBox="0 0 520 520" aria-hidden="true">
              <path className="draw" d="M 92 410 A 210 210 0 1 1 428 410" stroke={CRIMSON} strokeWidth="10" />
              <path d="M 130 440 A 230 230 0 0 0 200 476" stroke={GOLD} strokeWidth="6" />
            </svg>
            <HeroPhone />
            <div className="float-card float-chat" aria-hidden="true">
              <div className="who">Programming committee</div>
              <span className="mention">@Marcus</span> can you bring the banner Saturday?
            </div>
            <div className="float-card float-tap" aria-hidden="true">
              <span className="ic"><TapIcon /></span>
              <div><b>$25 paid at the door</b>Gala ticket · Tap to Pay</div>
            </div>
          </div>
        </div>
      </section>

      {/* Two ways in */}
      <section className="band" style={{ paddingTop: 24 }}>
        <div className="container">
          <div className="paths">
            <div className="path path-lead">
              <h2>Leading an organization?</h2>
              <p>Set up your community in an afternoon. Bring your logo, your chapters, and your roster.</p>
              <ul>
                {['Your own address and branding', 'Free trial, no card to start', 'Guided setup and CSV import'].map((x) => (
                  <li key={x}><CheckIcon aria-hidden="true" />{x}</li>
                ))}
              </ul>
              <Lnk to="/get-started" className="btn btn-gold">Create your community</Lnk>
            </div>
            <div className="path path-member">
              <h2>Already a member?</h2>
              <p className="muted">Your organization is already on {BRAND}. Sign in on the web, or download the app and search for your community.</p>
              <ul>
                {['One login for every community you belong to', 'Request to join, and your admins approve you', 'Works on iPhone, Android, and the web'].map((x) => (
                  <li key={x}><CheckIcon aria-hidden="true" />{x}</li>
                ))}
              </ul>
              <Lnk to="/sign-in" className="btn btn-primary">Find your community</Lnk>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="band band-alt">
        <div className="container">
          <div className="feat-head">
            <div>
              <h2 className="section-title">Everything a chapter runs on.</h2>
              <p className="section-lead">Replace the group texts, payment apps, sign-up sheets, and shared drives with one app your members actually open.</p>
            </div>
            <Lnk to="/features" className="btn btn-outline">See all features</Lnk>
          </div>
          <div className="feat-cols">
            {FEATURE_COLS.map((col) => (
              <div className="feat-col" key={col.h}>
                <h3>{col.h}</h3>
                <p>{col.d}</p>
                <ul className="feat-list">
                  {col.items.map(({ Icon, t, d }) => (
                    <li key={t}><Icon aria-hidden="true" /><div><b>{t}</b><span>{d}</span></div></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hierarchy */}
      <section className="band band-crimson">
        <div className="container hier">
          <div>
            <h2 className="section-title">Built for organizations with layers.</h2>
            <p>
              Headquarters, regions, and chapters each get their own space, admins, and dues,
              while members still belong to one organization.
            </p>
            <ul className="hier-points">
              <li><CheckIcon aria-hidden="true" />Send an announcement, event, or vote to one chapter, a whole region, or everyone.</li>
              <li><CheckIcon aria-hidden="true" />Posts aimed beyond your level go to the right admin for approval first.</li>
              <li><CheckIcon aria-hidden="true" />Each chapter keeps its own officers, roster, and calendar.</li>
            </ul>
          </div>
          <div className="tree" role="img" aria-label="Example structure: a national headquarters with a Pacific and an Atlantic region, each with two chapters. An announcement is targeted to the Pacific Region and its chapters.">
            <ul className="org" aria-hidden="true">
              <li>
                <span className="node hq">National HQ<small>12,400 members</small></span>
                <ul>
                  <li>
                    <span className="node target">Pacific Region</span>
                    <ul>
                      <li><span className="node target">Bay Area<small>Alumni</small></span></li>
                      <li><span className="node target">Westwood<small>Campus</small></span></li>
                    </ul>
                  </li>
                  <li>
                    <span className="node">Atlantic Region</span>
                    <ul>
                      <li><span className="node">Harbor City<small>Alumni</small></span></li>
                      <li><span className="node">Riverside<small>Campus</small></span></li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ul>
            <p className="tree-note" aria-hidden="true"><i />Announcement audience: Pacific Region and its chapters</p>
          </div>
        </div>
      </section>

      {/* Your brand */}
      <section className="band">
        <div className="container brands">
          <div>
            <h2 className="section-title">One app. Every community in its own colors.</h2>
            <p className="section-lead">
              Each community gets its own address at <strong>yourorg.{ROOT_DOMAIN}</strong>, its own logo
              and colors, and its own members. Belong to more than one? Switch between them without
              signing out.
            </p>
            <div className="audiences" style={{ marginTop: 22 }}>
              {SOLUTIONS.map((s) => <Lnk key={s.slug} to={`/solutions/${s.slug}`} className="pill">{s.name}</Lnk>)}
            </div>
          </div>
          <div className="minis" role="img" aria-label="Three example communities on Konklave, each with its own colors">
            {TENANTS.map((o) => (
              <div className="mini" key={o.name} aria-hidden="true">
                <div className="screen">
                  <div className="mini-head" style={{ background: o.tone }}>
                    <i style={{ borderColor: o.accent, color: o.accent }}>{o.short}</i>
                    <b>{o.name}</b>
                    <span>{o.host}.{ROOT_DOMAIN}</span>
                  </div>
                  <div className="mini-body"><div /><div /><div /></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Setup */}
      <section className="band band-alt">
        <div className="container">
          <h2 className="section-title">From sign-up to first announcement in an afternoon.</h2>
          <p className="section-lead">The setup wizard walks you through it. No sales call required.</p>
          <ol className="steps">
            {SETUP.map((s) => (
              <li key={s.t}><h3>{s.t}</h3><p>{s.d}</p></li>
            ))}
          </ol>
        </div>
      </section>

      {/* Final CTA */}
      <section className="band">
        <div className="container final">
          <img src={`${A}/logo-mark.png`} alt="" />
          <div>
            <h2>Give your members one place to gather.</h2>
            <p className="section-lead">
              Start a free trial for your organization, or <Lnk to="/contact" className="text-link">request a demo</Lnk> for
              a national rollout.
            </p>
            <div className="hero-cta">
              <Lnk to="/get-started" className="btn btn-primary">Create your community</Lnk>
              <Lnk to="/sign-in" className="btn btn-outline">Sign in</Lnk>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
